import {
	AfterViewInit,
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	filter,
	Observable,
	ReplaySubject,
	startWith,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../domain/diagnostic-errors.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { ProfileConfigType } from '../../domain/subscription.types';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	refreshSubscriptionColumnModels,
	refreshSubscriptionFilterModels,
	SubscriptionSelector,
} from '../../store';
import { ColumnModelsComponent } from '../column-models/column-models.component';
import { FilterModelsComponent } from '../filter-models/filter-models.component';
import {
	ProfileConfigFormControls,
	ProfileMetadataForm,
} from './profile-metadata.form';

type Mode = 'STEPPER' | 'STANDALONE';

export enum ProfileConfigStep {
	ProfileMetadata = 0,
	ColumnModel = 1,
	FilterModel = 2,
}

@Component({
	selector: 'app-profile-config',
	templateUrl: './profile-config.component.html',
	styleUrls: ['./profile-config.component.scss'],
	providers: [ProfileMetadataForm],
	inputs: ['profileConfig', 'mode', 'availableColumns'],
})
export class ProfileConfigComponent
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly Configuration = configuration;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly ProfileConfigFormControls = ProfileConfigFormControls;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ProfileConfigStep = ProfileConfigStep;
	protected readonly SubscriptionErrorKey = SubscriptionErrorKey;

	@Output()
	public forStepChange = new EventEmitter<number>();

	@ViewChild(ColumnModelsComponent)
	private readonly columnModelsCmp: ColumnModelsComponent | null = null;

	@ViewChild(FilterModelsComponent)
	private readonly filterModelsCmp: FilterModelsComponent | null = null;

	public Appearance: MatFormFieldAppearance = 'legacy';

	public vo$: Observable<{
		mode: Mode;
		profileConfig: ProfileItemEntity;
		columnModels: Map<string, ColumnModelItemEntity>;
		filterModels: Map<string, FilterModelItemEntity>;
		availableColumns: Map<string, ColumnConfigItemEntity>;
		currentStep: ProfileConfigStep;
		errors: Map<SubscriptionErrorKey, SubscriptionDiagnosticError>;
	}>;

	private readonly currentStep$ = new BehaviorSubject<ProfileConfigStep>(
		ProfileConfigStep.ProfileMetadata
	);

	private readonly filterModels$ = new BehaviorSubject<
		Map<string, FilterModelItemEntity>
	>(Map());

	private readonly columnModels$ = new BehaviorSubject<
		Map<string, ColumnModelItemEntity>
	>(Map());

	private readonly profileConfig$ = new ReplaySubject<ProfileItemEntity>(1);

	public constructor(
		public readonly profileConfigForm: ProfileMetadataForm,
		private readonly profileConfigProvider: SubscriptionProvider,
		private readonly selector: SubscriptionSelector,
		private readonly store: Store
	) {
		super();
		this.cmpId = 'profile-config';

		this.vo$ = combineAll({
			mode: this.toObservable<Mode>('mode'),
			profileConfig: this.profileConfig$,
			columnModels: this.columnModels$,
			filterModels: this.filterModels$,
			availableColumns:
				this.toObservable<Map<string, ColumnConfigItemEntity>>(
					'availableColumns'
				),
			currentStep: this.currentStep$,
			errors: this.profileConfigProvider.errors$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		Object.keys(this.profileConfigForm.form.controls).forEach((k) => {
			const ctrl = this.profileConfigForm.form.get(k) as AbstractControl;
			ctrl.markAsTouched();
		});

		this.handleSubscription();
	}

	public refreshColumnModels(): void {
		this.store.dispatch(refreshSubscriptionColumnModels());
	}

	public refreshFilterModels(): void {
		this.store.dispatch(refreshSubscriptionFilterModels());
	}

	public nextStep(currentStep: ProfileConfigStep): void {
		if (currentStep === ProfileConfigStep.ProfileMetadata) {
			this.currentStep$.next(ProfileConfigStep.ColumnModel);
		}

		if (currentStep === ProfileConfigStep.ColumnModel) {
			this.currentStep$.next(ProfileConfigStep.FilterModel);
		}
	}

	public previousStep(currentStep: ProfileConfigStep): void {
		if (currentStep === ProfileConfigStep.ColumnModel) {
			this.currentStep$.next(ProfileConfigStep.ProfileMetadata);
			return;
		}

		if (currentStep === ProfileConfigStep.FilterModel) {
			this.currentStep$.next(ProfileConfigStep.ColumnModel);
			return;
		}
	}

	public setStep(step: ProfileConfigStep): void {
		this.currentStep$.next(step);
	}

	public getProfileConfig(): ProfileConfigType | null {
		if (!this.columnModelsCmp) {
			return null;
		}
		if (!this.filterModelsCmp) {
			return null;
		}
		return {
			metadata: this.profileConfigForm.extract(),
			columnModels: this.columnModelsCmp.getSelectedColumnModels(),
			filterModels: this.filterModelsCmp.getSelectedFilterModels(),
		};
	}

	private handleSubscription(): void {
		this.currentStep$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((step) => this.forStepChange.emit(step))
			)
			.subscribe();

		this.toObservable<ProfileItemEntity>('profileConfig')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((config) => {
					this.profileConfigForm.populate(config);
					this.profileConfig$.next(config);
				})
			)
			.subscribe();

		this.profileConfigForm.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				startWith(this.profileConfigForm.form.value),
				tap(() => {
					if (!this.profileConfigForm.form.valid) {
						this.profileConfigProvider.addError(
							SubscriptionDiagnosticError.build({
								key: SubscriptionErrorKey.ProfileMissingFields,
								type: 'critic',
								message: $localize`:i18n=@@subscription.diagnosticError.profile.missingFields:`,
							})
						);
					} else {
						this.profileConfigProvider.removeError(
							SubscriptionErrorKey.ProfileMissingFields
						);
					}
				})
			)
			.subscribe();

		combineAll({
			columnModels: this.selector.getSubscriptionColumnModels$(),
			availableColumns:
				this.toObservable<Map<string, ColumnConfigItemEntity>>(
					'availableColumns'
				),
			mode: this.toObservable<Mode>('mode'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.profileConfig$),
				tap(([{ columnModels, availableColumns, mode }, profile]) => {
					if (mode === 'STEPPER') {
						const columns = Set(
							availableColumns
								.map((col) => col.setIsActive(true))
								.toList()
								.toArray()
						);
						const defaultColumnModel =
							ColumnModelItemEntity.createDefaultColumnModel(columns);

						this.columnModels$.next(
							Map<string, ColumnModelItemEntity>({
								[defaultColumnModel.id]: defaultColumnModel,
							})
						);
						this.profileConfig$.next(
							profile.setColumnModelId(defaultColumnModel.id)
						);
					} else {
						this.columnModels$.next(columnModels);
					}
				})
			)
			.subscribe();

		this.selector
			.getSubscriptionFilterModels$()
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(([, mode]) => mode !== 'STEPPER'),
				tap(([filterModels]) => {
					this.filterModels$.next(filterModels);
				})
			)
			.subscribe();
	}
}
